
import React, { useState, useRef } from 'react';
import { SimulationConfig, CustomMelody, MelodyData } from '@/types';
import { AUDIO } from '@/services/AudioService';
import { CloudService } from '@/services/CloudService';
import { FileMusic, Upload, Trash2, Music, Sparkles, Wand2, Keyboard } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';

import { InfoPopup } from './InfoPopup';

interface AudioWorkshopTabProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const AudioWorkshopTab: React.FC<AudioWorkshopTabProps> = ({ config, setConfig }) => {
  const [manualMelodyString, setManualMelodyString] = useState<string>("");
  const [melodyName, setMelodyName] = useState<string>("");
  const [detectedVoices, setDetectedVoices] = useState<string[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>("");
  const midiInputRef = useRef<HTMLInputElement>(null);

  const handleMidiUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const loadingToast = toast.loading(`Parsing ${file.name}...`);
    try {
      const midiData = await AUDIO.parseMidiFile(file);
      const uploadRes = await CloudService.uploadFile(file);
      const uploadedUrl = uploadRes?.url;
      const uploadedSize = uploadRes?.size;
      
      const tempId = Math.random().toString(36).substring(7);
      const newMelody: CustomMelody = {
        id: tempId,
        name: file.name.replace(/\.mid$/i, '').substring(0, 45),
        frequencies: [],
        midiData,
        format: 'MIDI',
        url: uploadedUrl || undefined
      };
      
      const updatedConfig = {
        ...config,
        customMelodies: [...config.customMelodies, newMelody],
        selectedMelody: newMelody.id,
        enableRhythmic: true
      };
      setConfig(updatedConfig);
      const cloudRes = await CloudService.registerMelody(newMelody.name, newMelody, uploadedSize);
      if (cloudRes && !cloudRes.error) {
        toast.success("MIDI imported and saved to cloud", { id: loadingToast });
      } else {
        toast.info("MIDI imported locally (Server limit exceeded)", { id: loadingToast });
      }
      if (uploadedUrl) await CloudService.registerSong(newMelody.name, uploadedUrl, undefined, uploadedSize);
    } catch (err: any) {
      console.error("MIDI parse failed", err);
      toast.error(`Save failed: ${err.message || 'Check connection or file size'}`, { id: loadingToast });
    }
    if (midiInputRef.current) midiInputRef.current.value = "";
  };

  const deleteMelody = async (id: string) => {
    const loadingToast = toast.loading("Deleting melody...");
    try {
        const res = await CloudService.deleteMelody(id);
        const resSong = await CloudService.deleteSong(id);
        
        if (res || resSong) {
            setConfig(prev => ({
              ...prev,
              customMelodies: prev.customMelodies.filter(m => m.id !== id)
            }));
            toast.success("Melody deleted", { id: loadingToast });
        } else {
            // Even if it failed on server, remove locally to be helpful, 
            // but inform the user.
            setConfig(prev => ({
              ...prev,
              customMelodies: prev.customMelodies.filter(m => m.id !== id)
            }));
            toast.info("Removed locally. Could not verify server deletion.", { id: loadingToast });
        }
    } catch (err) {
        toast.error("Failed to delete from cloud", { id: loadingToast });
    }
  };

  const applyManualMelody = async (save: boolean = false) => {
    const trimmed = manualMelodyString.trim();
    if (!trimmed) return;

    const loadingToast = save ? toast.loading("Processing melody...") : null;

    if (trimmed.startsWith('TVRoZA') || trimmed.includes('data:audio/midi;base64,') || trimmed.startsWith('Online Sequencer:')) {
        try {
            let midiData;
            if (trimmed.startsWith('Online Sequencer:')) {
                midiData = AUDIO.parseOnlineSequencerString(trimmed);
            } else {
                midiData = AUDIO.parseMidiBase64(trimmed, melodyName || 'Pasted MIDI');
            }

            const tempId = Math.random().toString(36).substring(7);
            const newMelody: CustomMelody = {
                id: tempId,
                name: (melodyName || 'Pasted MIDI').trim().substring(0, 45),
                frequencies: [],
                midiData,
                format: 'MIDI'
            };
            
            const updatedConfig = {
                ...config,
                customMelodies: [...config.customMelodies, newMelody],
                selectedMelody: newMelody.id,
                enableRhythmic: true
            };
            setConfig(updatedConfig);
            if (save) {
                const res = await CloudService.registerMelody(newMelody.name, newMelody);
                if (res && !res.error) {
                    toast.success("Saved to cloud library", { id: loadingToast! });
                } else {
                    toast.error(res?.error || "Failed to save to cloud", { id: loadingToast! });
                }
            }
            if (!save) toast.success("Sequence preview active");
            setMelodyName("");
            setManualMelodyString("");
            return;
        } catch (e: any) {
            console.error(e);
            if (loadingToast) toast.error("Parsing failed: " + e.message, { id: loadingToast });
            else toast.error("Parsing failed");
        }
    }

    const freqs = AUDIO.parseMelodyString(manualMelodyString);
    if (freqs.length > 0) {
        if (save && melodyName.trim()) {
            const tempId = Math.random().toString(36).substring(7);
            const newMelody: CustomMelody = {
                id: tempId,
                name: melodyName.trim().substring(0, 45),
                frequencies: freqs,
                format: 'FREQUENCIES'
            };
            
            const updatedConfig = {
                ...config,
                customMelodies: [...config.customMelodies, newMelody],
                selectedMelody: newMelody.id,
                enableRhythmic: true
            };
            setConfig(updatedConfig);
            const res = await CloudService.registerMelody(newMelody.name, newMelody);
            if (res && !res.error) {
                toast.success("Saved to cloud library", { id: loadingToast! });
            } else {
                toast.error(res?.error || "Failed to save to cloud", { id: loadingToast! });
            }
            setMelodyName("");
            setManualMelodyString("");
        } else {
            setConfig(prev => ({ 
                ...prev, 
                customFrequencies: freqs, 
                enableRhythmic: true,
                selectedMelody: 'Custom (Uploaded)'
            }));
            toast.success("Frequency preview active");
        }
        AUDIO.resetIndex();
    } else {
        if (loadingToast) toast.error("No valid frequency data found", { id: loadingToast });
    }
  };

  return (
    <div className={`flex flex-col gap-8`}>
      {/* Workshop Input */}
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 ml-1">
              <span className="text-[10px] font-display font-black uppercase tracking-[0.2em] opacity-40">Melody Name</span>
              <InfoPopup settingKey="workshopSequenceName" darkMode={config.darkMode} />
            </div>
            <input 
              type="text" 
              value={melodyName}
              onChange={e => setMelodyName(e.target.value)}
              placeholder="E.g. Lunar Surface"
              className={`w-full h-12 px-5 rounded-2xl text-[11px] font-black uppercase tracking-widest outline-none transition-all ${config.darkMode ? 'bg-black/30 border-white/5 text-white placeholder:text-zinc-700' : 'bg-white border-zinc-950/5 text-zinc-950 placeholder:text-zinc-300 shadow-inner'}`}
            />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 ml-1">
              <span className="text-[10px] font-display font-black uppercase tracking-[0.2em] opacity-40">MIDI or Frequency Data</span>
              <InfoPopup settingKey="workshopDataStream" darkMode={config.darkMode} />
            </div>
            <textarea 
              value={manualMelodyString} 
              onChange={e => setManualMelodyString(e.target.value)}
              className={`w-full h-32 p-5 rounded-[2rem] text-[12px] font-mono outline-none transition-all border leading-relaxed resize-none ${config.darkMode ? 'bg-black/30 border-white/5 text-neon-cyan' : 'bg-white border-zinc-950/5 text-zinc-950 shadow-inner focus:shadow-sm'}`}
              placeholder={`Paste MIDI Base64 or [261, 329, 392] ...`}
            />
          </div>

          <div className="flex gap-2">
            <button 
              onClick={() => applyManualMelody(false)}
              className={`flex-1 h-12 rounded-xl text-[10px] font-display font-black uppercase tracking-widest transition-all active:scale-95 flex items-center justify-center gap-2 ${config.darkMode ? 'bg-white/5 text-white hover:bg-white/10' : 'bg-zinc-200 text-zinc-950 hover:bg-zinc-300'}`}
            >
              <Wand2 className="w-3.5 h-3.5" />
              Preview Notes
            </button>
            <button 
              onClick={() => applyManualMelody(true)}
              disabled={!melodyName.trim()}
              className={`flex-1 h-12 rounded-xl text-[10px] font-medium uppercase tracking-[0.2em] transition-all active:scale-95 flex items-center justify-center gap-2 ${melodyName.trim() ? (config.darkMode ? 'bg-white border-white text-zinc-950 shadow-md' : 'bg-white border-cyan-600/30 text-cyan-600 shadow-md') : 'bg-zinc-100 text-zinc-400 cursor-not-allowed opacity-50'}`}
            >
              <Sparkles className="w-4 h-4" />
              Save to Cloud
            </button>
          </div>
        </div>

      {/* MIDI Upload */}
      <div className="px-1 relative">
        <div className="absolute -top-3 right-4 z-10">
          <InfoPopup settingKey="midiHardwareImport" darkMode={config.darkMode} />
        </div>
        <input 
          type="file" 
          accept=".mid,.midi" 
          className="hidden" 
          ref={midiInputRef}
          onChange={handleMidiUpload}
        />
        <button 
          onClick={() => midiInputRef.current?.click()}
          className={`w-full h-14 rounded-2xl flex items-center justify-center gap-3 transition-all active:scale-95 border ${config.darkMode ? 'border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10' : 'bg-emerald-50 text-emerald-600 border-emerald-500/20 hover:bg-emerald-100 shadow-sm'}`}
        >
          <Upload className="w-4 h-4" />
          <span className="text-[10px] font-display font-black uppercase tracking-widest">Upload MIDI File</span>
        </button>
      </div>

      {/* Library */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
              {config.customMelodies.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center opacity-20 border-2 border-dashed border-zinc-100 rounded-[2.5rem] bg-zinc-50/30">
              <Music className="w-8 h-8 mb-4" />
              <span className="text-[10px] font-medium uppercase tracking-[0.25em]">No Custom Melodies</span>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {config.customMelodies.slice().reverse().map(m => (
                <div 
                  key={m.id} 
                  className={`group p-5 rounded-[2rem] border transition-all flex items-center justify-between ${config.selectedMelody === m.name ? (config.darkMode ? 'bg-white border-white text-zinc-950' : 'bg-white border-cyan-600/30 text-zinc-950 shadow-md') : (config.darkMode ? 'bg-white/5 border-white/5 text-white/60' : 'bg-zinc-50 border-zinc-100 text-zinc-500 shadow-sm')}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${config.selectedMelody === m.name ? 'bg-cyan-500/10 text-cyan-600' : 'bg-white text-zinc-300 shadow-sm border border-zinc-100'}`}>
                      {m.format === 'MIDI' ? <Keyboard className="w-5 h-5" /> : <Music className="w-5 h-5" />}
                    </div>
                    <div className="flex flex-col">
                      <span className={`text-[11px] font-medium uppercase tracking-widest ${config.selectedMelody === m.name ? 'text-zinc-950' : 'text-zinc-600 group-hover:text-zinc-950 transition-colors duration-300'}`}>{m.name}</span>
                      <span className="text-[8px] font-mono uppercase opacity-40 mt-0.5">{m.frequencies.length || m.midiData?.notes.length} Fragments</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setConfig(p => ({ ...p, selectedMelody: m.name, enableRhythmic: true }))}
                      className={`h-8 px-4 rounded-lg text-[9px] font-medium uppercase tracking-[0.15em] transition-all active:scale-90 ${config.selectedMelody === m.name ? 'bg-neon-cyan text-white shadow-xl shadow-cyan-500/20 font-black' : 'bg-white border border-zinc-200 text-zinc-500 hover:bg-zinc-50 shadow-sm'}`}
                    >
                      {config.selectedMelody === m.name ? 'Loaded' : 'Load'}
                    </button>
                    <button 
                      onClick={() => deleteMelody(m.id)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all active:scale-90 ${config.selectedMelody === m.name ? 'hover:bg-white/10 text-white/40 hover:text-red-400' : 'hover:bg-red-50 text-zinc-300 hover:text-red-500'}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
